import React, { useState, useEffect } from 'react';

interface HeaderProps {
  logoLight?: string;
  logoDark?: string;
}

export const Header: React.FC<HeaderProps> = ({
  logoLight = '',
  logoDark = ''
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Get WordPress data
  const wpData = (window as any).naiThemeData || {};

  useEffect(() => {
    const updateScrollState = () => {
      const shouldBeScrolled = window.scrollY > 50;
      if (shouldBeScrolled !== isScrolled) {
        setIsScrolled(shouldBeScrolled);
      }
    };

    const handleScroll = () => {
      window.requestAnimationFrame(updateScrollState);
    };

    window.addEventListener('scroll', handleScroll);
    updateScrollState();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  // Dynamic logo paths - NO HARDCODING
  const logoLightSrc = logoLight || '/public/content/images/peak-builders-roofers-san-diego-logo-white.webp';
  const logoDarkSrc = logoDark || '/public/content/images/peak-builders-roofers-san-diego-logo-black.webp';

  // Determine header state - exact vibe logic
  const headerClasses = `fixed top-0 left-0 right-0 z-50 py-2 transition-all duration-300 ${isScrolled ? 'header-scrolled' : ''}`;
  const currentLogo = isScrolled ? logoDarkSrc : logoLightSrc;

  // Get site data dynamically
  const siteName = wpData.siteName || 'Website';
  const sitePhone = wpData.phone || '';

  return (
    <>
      <header id="main-header" className={headerClasses}>
        <div className="w-full px-6 md:px-12">
          <nav className="flex items-center justify-between w-full">
            {/* Logo */}
            <a href="/" className="flex-shrink-0 z-10">
              <img
                id="logo-img"
                src={currentLogo}
                alt={`${siteName} Logo`}
                className="h-12 w-auto transition-all duration-300"
                width="120"
                height="48"
              />
            </a>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center justify-center flex-grow">
              <ul id="nav-links" className="flex items-center space-x-6 font-light text-sm tracking-wide text-white">
                {/* Services Dropdown */}
                <li className="relative group">
                  <button className="uppercase py-2 transition-colors duration-300 flex items-center focus:outline-none" id="services-dropdown-btn">
                    Services
                    <svg className="w-4 h-4 ml-1 transition-transform duration-300 group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  {/* Dropdown Menu */}
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white/95 backdrop-blur-sm rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-1 group-hover:translate-y-0 z-50">
                    <div className="py-2">
                      <a href="/roofing-contractors" className="block px-4 py-2 text-gray-700 font-light hover:bg-gray-50 hover:text-blue-600 transition-colors duration-150">
                        Roofing Contractors
                      </a>
                      <a href="/remodeling" className="block px-4 py-2 text-gray-700 font-light hover:bg-gray-50 hover:text-blue-600 transition-colors duration-150">
                        Remodeling
                      </a>
                      <a href="/construction-main" className="block px-4 py-2 text-gray-700 font-light hover:bg-gray-50 hover:text-blue-600 transition-colors duration-150">
                        New Construction
                      </a>
                    </div>
                  </div>
                </li>

                <li className="relative group">
                  <a href="/financing" className="uppercase py-2 transition-colors duration-300">Financing</a>
                </li>

                <li className="relative group">
                  <a href="/about-us" className="uppercase py-2 transition-colors duration-300">About Us</a>
                </li>

                <li className="relative group">
                  <a href="/latest-projects" className="uppercase py-2 transition-colors duration-300">Latest Projects</a>
                </li>

                <li className="relative group">
                  <a href="/contact" className="uppercase py-2 transition-colors duration-300">Contact</a>
                </li>

                <li className="relative group">
                  <a href="/blog" className="uppercase py-2 transition-colors duration-300">Blog</a>
                </li>
              </ul>
            </div>

            {/* CTA Button - Blue Glassmorphic */}
            <div className="hidden lg:flex items-center ml-8">
              {sitePhone ? (
                <a href={`tel:${sitePhone.replace(/[^0-9]/g, '')}`} className="bg-blue-600/90 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-full hover:bg-blue-500/90 transition-all duration-300 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                  {sitePhone}
                </a>
              ) : (
                <a href="tel:6193308185" className="bg-blue-600/90 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-full hover:bg-blue-500/90 transition-all duration-300 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                  (619) 330-8185
                </a>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                id="mobile-menu-button"
                className="text-white focus:outline-none p-2"
                onClick={() => setIsMobileOpen(!isMobileOpen)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                </svg>
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Menu */}
      <div id="mobile-menu" className={`${isMobileOpen ? '' : 'hidden'} fixed inset-0 bg-gray-900 text-white z-50 lg:hidden`}>
        <div className="flex flex-col h-full w-full bg-gray-900">
          {/* Mobile Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-700 bg-gray-900">
            <a href="/" className="flex items-center">
              <img src="/public/content/images/logo-white.webp" alt={`${siteName} Logo`} className="h-12 w-auto" width="120" height="48" />
            </a>
            <button
              id="close-mobile-menu"
              className="focus:outline-none p-2 text-white"
              onClick={() => setIsMobileOpen(false)}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* Mobile Navigation */}
          <div className="flex-1 overflow-y-auto bg-gray-900">
            <nav className="p-6">
              <ul className="space-y-4">
                <li>
                  <a href="/" className="block py-3 text-xl font-light text-white hover:text-blue-400 transition-colors duration-300">Home</a>
                </li>

                {/* Mobile Services Dropdown */}
                <li>
                  <a href="/roofing-contractors" className="block py-3 text-xl font-light text-white hover:text-blue-400 transition-colors duration-300">Roofing Contractors</a>
                </li>
                <li>
                  <a href="/remodeling" className="block py-3 text-xl font-light text-white hover:text-blue-400 transition-colors duration-300">Remodeling</a>
                </li>
                <li>
                  <a href="/construction-main" className="block py-3 text-xl font-light text-white hover:text-blue-400 transition-colors duration-300">New Construction</a>
                </li>

                <li>
                  <a href="/financing" className="block py-3 text-xl font-light text-white hover:text-blue-400 transition-colors duration-300">Financing</a>
                </li>

                <li>
                  <a href="/about-us" className="block py-3 text-xl font-light text-white hover:text-blue-400 transition-colors duration-300">About Us</a>
                </li>

                <li>
                  <a href="/latest-projects" className="block py-3 text-xl font-light text-white hover:text-blue-400 transition-colors duration-300">Latest Projects</a>
                </li>

                <li>
                  <a href="/contact" className="block py-3 text-xl font-light text-white hover:text-blue-400 transition-colors duration-300">Contact</a>
                </li>

                <li>
                  <a href="/blog" className="block py-3 text-xl font-light text-white hover:text-blue-400 transition-colors duration-300">Blog</a>
                </li>
              </ul>
            </nav>
          </div>

          {/* Mobile Menu Footer - Trust Indicators Only */}
          <div className="p-4 border-t border-gray-700 bg-gray-900">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-center space-x-4 text-gray-300">
                <div className="flex items-center">
                  <span className="text-yellow-400">★★★★★</span>
                  <span className="ml-1 text-sm">5.0</span>
                </div>
                <div className="w-px h-4 bg-gray-600"></div>
                <span className="text-sm font-semibold">Licensed & Insured</span>
                <div className="w-px h-4 bg-gray-600"></div>
                <span className="text-sm font-semibold">2,000+ Jobs</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};